import { useAtomValue, useSetAtom } from 'jotai'
import { scheduleCurrencyAtom } from '../../atoms/reviewPricing.atoms'
import {
  GreyGrey60,
  Primary13Regular,
  Section,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { SchedulePriceTable } from '../SchedulePriceTable'
import { PhaseDateFields } from './PhaseDateFields'
import { usePhase } from '../../utils/usePhase'
import { PhaseKebabMenu } from './PhaseKebabMenu'
import { PriceKebabMenu } from './PriceKebabMenu'
import { DiscountKebabMenu } from './DiscountKebabMenu'
import { MinimumKebabMenu } from './MinimumKebabMenu'
import { PhaseAddEntityKebabMenu } from './PhaseAddEntityKebabMenu'
import { isEditingPhaseNameAtom, PhaseLabel } from './PhaseLabel'
import { ScopeProvider } from 'jotai-scope'
import { showValidationErrorsAtom } from '../../atoms/reviewPricing.validation.atoms'
import { PhaseRecurrencePreferenceField } from './PhaseRecurrencePreferenceField'
import { FC } from 'react'
import { taskDrawerConfigAtom } from '../../../../../atoms'
import { HighlightFieldPathsOnHover } from '../../../../HighlightFieldPathsOnHover'

export const Phase: FC<{
  phaseId: string
}> = ({ phaseId }) => {
  const { phase } = usePhase(phaseId)
  const setDrawer = useSetAtom(taskDrawerConfigAtom)
  const scheduleCurrency = useAtomValue(scheduleCurrencyAtom)
  const showValidationErrors = useAtomValue(showValidationErrorsAtom)

  const phaseFieldPath = `billingSchedule.phases[${phase.boundingBoxIndex}]`
  return (
    <HighlightFieldPathsOnHover fieldPaths={[`${phaseFieldPath}.*`]}>
      <ScopeProvider atoms={[isEditingPhaseNameAtom]}>
        <Box data-testid={`reviewPricing.phase.${phase.id}`}>
          <Grid
            width="100%"
            columnGap="8px"
            height="32px"
            marginBottom="8px"
            gridTemplateColumns="1fr auto"
            alignItems="center"
          >
            <Flex gap="8px">
              <HighlightFieldPathsOnHover
                fieldPaths={[`${phaseFieldPath}.name`]}
              >
                <PhaseLabel phaseId={phase.id} />
              </HighlightFieldPathsOnHover>
            </Flex>

            <Flex gap="6px">
              <PhaseAddEntityKebabMenu phaseId={phase.id} />
              <PhaseKebabMenu phaseId={phase.id} />
            </Flex>
          </Grid>

          <Section
            variant="emphasized"
            marginBottom="8px"
            display="flex"
            gap="16px"
            alignItems="flex-end"
          >
            <PhaseDateFields
              phaseId={phase.id}
              phaseFieldPath={phaseFieldPath}
            />
            <Box paddingBottom="8px">
              <PhaseRecurrencePreferenceField phaseId={phase.id} />
            </Box>
          </Section>
          <SchedulePriceTable>
            <SchedulePriceTable.Header />
            {phase.minimumGroups.length ? (
              phase.minimumGroups.map(group => (
                <HighlightFieldPathsOnHover
                  key={group.id}
                  fieldPaths={[
                    `${phaseFieldPath}.minimums[${group.boundingBoxIndex}].*`
                  ]}
                >
                  {!!group.minimum && (
                    <SchedulePriceTable.Row
                      onClick={() =>
                        setDrawer({
                          type: 'MINIMUM',
                          phaseId: phase.id,
                          minimumId: group.minimum?.id
                        })
                      }
                      key={group.id}
                      description={
                        <SchedulePriceTable.Cell
                          value="Minimum commitment"
                          data-testid={`reviewPricing.phase.${phase.id}.minimum.description`}
                        />
                      }
                      amount={
                        <SchedulePriceTable.MinimumCell
                          amount={group.minimum.amount}
                          currency={scheduleCurrency}
                          billingFrequency={group.minimum.billingFrequency}
                          data-testid={`reviewPricing.phase.${phase.id}.minimum.amount`}
                        />
                      }
                      actions={
                        <SchedulePriceTable.ActionCell>
                          <MinimumKebabMenu
                            phaseId={phase.id}
                            minimumId={group.minimum.id}
                            data-testid={`reviewPricing.phase.${phase.id}.minimum.menu`}
                          />
                        </SchedulePriceTable.ActionCell>
                      }
                    />
                  )}
                  {group.prices.map(price => {
                    return (
                      <HighlightFieldPathsOnHover
                        key={`${phase.id}-${price.id}`}
                        fieldPaths={[
                          `billingSchedule.prices[${price.boundingBoxIndex}].*`
                        ]}
                      >
                        <SchedulePriceTable.Row
                          onClick={() =>
                            setDrawer({
                              type: 'PRICE',
                              phaseId: phase.id,
                              priceId: price.id
                            })
                          }
                          hasMinimum={!!group.minimum}
                          key={`${phase.id}-${price.id}`}
                          hasError={showValidationErrors && !price.productId}
                          description={
                            price.productId ? (
                              <SchedulePriceTable.PriceNameCell
                                name={price.name}
                                hasMinimum={!!group.minimum}
                                data-testid={`reviewPricing.phase.${phase.id}.price.${price.id}.name`}
                              />
                            ) : (
                              <SchedulePriceTable.SelectProductCell
                                phaseId={phase.id}
                                priceId={price.id}
                                hasMinimum={!!group.minimum}
                                data-testid={`reviewPricing.phase.${phase.id}.price.${price.id}.selectProduct`}
                              />
                            )
                          }
                          billingType={
                            <HighlightFieldPathsOnHover
                              fieldPaths={[
                                `billingSchedule.prices[${price.boundingBoxIndex}].billingType`
                              ]}
                            >
                              <SchedulePriceTable.BillingTypeCell
                                billingType={price.billingType}
                                data-testid={`reviewPricing.phase.${phase.id}.price.${price.id}.billingType`}
                              />
                            </HighlightFieldPathsOnHover>
                          }
                          billingFrequency={
                            <HighlightFieldPathsOnHover
                              fieldPaths={[
                                `billingSchedule.prices[${price.boundingBoxIndex}].billingFrequency`
                              ]}
                            >
                              <SchedulePriceTable.BillingFrequencyCell
                                billingFrequency={price.billingFrequency}
                                data-testid={`reviewPricing.phase.${phase.id}.price.${price.id}.billingFrequency`}
                              />
                            </HighlightFieldPathsOnHover>
                          }
                          discount={
                            !!price.discount && (
                              <HighlightFieldPathsOnHover
                                fieldPaths={[
                                  `${phaseFieldPath}.discounts[${price.discount.boundingBoxIndex}].*`
                                ]}
                              >
                                <SchedulePriceTable.DiscountCell
                                  amount={price.discount.amount}
                                  discountType={price.discount.type}
                                  currency={scheduleCurrency}
                                  data-testid={`reviewPricing.phase.${phase.id}.price.${price.id}.discount`}
                                  onClick={() =>
                                    setDrawer({
                                      type: 'DISCOUNT',
                                      phaseId: phase.id,
                                      discountId: price.discount?.id
                                    })
                                  }
                                />
                              </HighlightFieldPathsOnHover>
                            )
                          }
                          amount={
                            <SchedulePriceTable.PriceCell
                              price={price}
                              ownDiscount={price.discount}
                              data-testid={`reviewPricing.phase.${phase.id}.price.${price.id}.amount`}
                            />
                          }
                          actions={
                            <SchedulePriceTable.ActionCell>
                              <PriceKebabMenu
                                phaseId={phase.id}
                                priceId={price.id}
                                discountId={price.discount?.id}
                                data-testid={`reviewPricing.phase.${phase.id}.price.${price.id}.menu`}
                              />
                            </SchedulePriceTable.ActionCell>
                          }
                        />
                      </HighlightFieldPathsOnHover>
                    )
                  })}
                </HighlightFieldPathsOnHover>
              ))
            ) : (
              <Flex
                {...Primary13Regular}
                color={GreyGrey60}
                alignItems="center"
                justifyContent="center"
                padding="8px"
              >
                No pricing found for phase
              </Flex>
            )}
            {!!phase.globalDiscount && (
              <SchedulePriceTable.Row
                onClick={() =>
                  setDrawer({
                    type: 'DISCOUNT',
                    phaseId: phase.id,
                    discountId: phase.globalDiscount?.id
                  })
                }
                description={
                  <SchedulePriceTable.Cell
                    value="Global discount"
                    data-testid={`reviewPricing.phase.${phase.id}.globalDiscount`}
                  />
                }
                discount={
                  <HighlightFieldPathsOnHover
                    fieldPaths={[`${phaseFieldPath}.discounts*`]}
                  >
                    <SchedulePriceTable.DiscountCell
                      amount={phase.globalDiscount.amount}
                      discountType={phase.globalDiscount.type}
                      currency={scheduleCurrency}
                      data-testid={`reviewPricing.phase.${phase.id}.globalDiscount.amount`}
                    />
                  </HighlightFieldPathsOnHover>
                }
                actions={
                  <SchedulePriceTable.ActionCell>
                    <DiscountKebabMenu
                      phaseId={phase.id}
                      discountId={phase.globalDiscount.id}
                      data-testid={`reviewPricing.phase.${phase.id}.globalDiscount.menu`}
                    />
                  </SchedulePriceTable.ActionCell>
                }
              />
            )}
          </SchedulePriceTable>
        </Box>
      </ScopeProvider>
    </HighlightFieldPathsOnHover>
  )
}
